import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  getCompetitorAdminOverview,
  regenerateAllCompetitorPages,
  regenerateCompetitorPage,
  setCompetitorOverride,
  type CompetitorAdminOverview,
} from "@/lib/competitor-seo.functions";

export const Route = createFileRoute("/dashboard/admin/seo-competitor")({
  head: () => ({
    meta: [
      { title: "Competitor SEO · Heallexa Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CompetitorSeoPage,
});

type Overview = CompetitorAdminOverview;

function CompetitorSeoPage() {
  const { user, loading: authLoading, primaryRole } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<Overview | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const fetchOverview = useServerFn(getCompetitorAdminOverview);
  const regenAll = useServerFn(regenerateAllCompetitorPages);
  const regenOne = useServerFn(regenerateCompetitorPage);
  const setOverride = useServerFn(setCompetitorOverride);

  useEffect(() => {
    if (authLoading) return;
    if (!user || primaryRole !== "admin") {
      navigate({ to: "/admin-login" });
      return;
    }
    fetchOverview().then(setData).catch((e) => toast.error(e.message));
  }, [user, authLoading, primaryRole, navigate, fetchOverview]);

  const refresh = async () => {
    setData(await fetchOverview());
  };

  const onRegenAll = async () => {
    setBusy("all");
    try {
      const r = await regenAll();
      toast.success(`Regenerated ${r.processed} pages`);
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const onRegenOne = async (slug: string) => {
    setBusy(slug);
    try {
      await regenOne({ data: { slug } });
      toast.success(`Regenerated ${slug}`);
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const onOverride = async (slug: string, override: "force_index" | "force_noindex" | "clear") => {
    setBusy(slug);
    try {
      await setOverride({ data: { slug, override } });
      toast.success(`Updated ${slug}`);
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (authLoading || !data) {
    return (
      <div>

        <div className="container mx-auto p-8 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  return (
    <AdminPageShell
      title="Competitor SEO"
      description="Programmatic comparison pages: indexing status, thin-content risk, and overrides."
      primaryAction={{ label: busy === "all" ? "Regenerating…" : "Regenerate all", icon: RefreshCw, onClick: onRegenAll }}
    >
      <div>


        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            ["Total", data.totals.total],
            ["Indexed", data.totals.indexed],
            ["Noindex", data.totals.noindex],
            ["Thin (city <10)", data.totals.thin],
            ["City mismatch", data.totals.cityMismatchRisk],
          ].map(([label, value]) => (
            <div key={label as string} className="border border-border bg-card p-4 rounded-md card-surface-flat">
              <div className="text-xs text-muted-foreground">{label}</div>
              <div className="text-2xl font-bold">{value as number}</div>
            </div>
          ))}
        </div>

        <div className="border border-border bg-card overflow-x-auto rounded-md card-surface">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="p-3">Slug</th>
                <th className="p-3">Kind</th>
                <th className="p-3">Providers</th>
                <th className="p-3">Indexable</th>
                <th className="p-3">Reason / Override</th>
                <th className="p-3">Refreshed</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.slug} className="border-t border-border">
                  <td className="p-3 font-mono text-xs">{r.slug}</td>
                  <td className="p-3">{r.page_kind}</td>
                  <td className="p-3">{r.provider_count ?? 0}</td>
                  <td className="p-3">
                    <span className={r.is_indexable ? "text-primary" : "text-muted-foreground"}>
                      {r.is_indexable ? "yes" : "no"}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {r.manual_override ? `override: ${r.manual_override}` : r.noindex_reason ?? "—"}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {r.last_refreshed_at?.slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1 flex-wrap">
                      <a
                        href={`/alternatives/${r.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center text-xs px-2 py-1 border border-border hover:bg-muted rounded-md"
                      >
                        <ExternalLink className="h-3 w-3 mr-1" /> View
                      </a>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy === r.slug}
                        onClick={() => onRegenOne(r.slug)}
                      >
                        Regen
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy === r.slug}
                        onClick={() => onOverride(r.slug, "force_index")}
                      >
                        Index
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy === r.slug}
                        onClick={() => onOverride(r.slug, "force_noindex")}
                      >
                        Noindex
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy === r.slug || !r.manual_override}
                        onClick={() => onOverride(r.slug, "clear")}
                      >
                        Clear
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminPageShell>
  );
}
